import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { updateWebsite } from 'src/editor/api/website-websites-api'
import { FeatureFlagsEnum } from 'src/editor/shared/growthbook/feature-flags-enum'
import { useFeatureFlag } from 'src/editor/shared/growthbook/use-feature-flag'
import { useUpdateStoreObject } from 'src/editor/shared/hooks/use-update-store'
import {
  setWebsiteDataBaseline,
  setWebsiteDraftTheme,
  setWebsiteTheme,
} from 'src/editor/store/website-settings/website-settings-actions'
import {
  getWebsiteDataBaseline,
  getWebsiteDraftTheme,
  getWebsiteLocale,
  getWebsiteName,
} from 'src/editor/store/website-settings/website-settings-selector'
import { useWebsiteSettings } from '../../../client/store'
import { useWebsiteSettingsForSave } from './use-website-settings-for-save'

export const useWebsiteData = () => {
  const dispatch = useDispatch()

  const isWebsiteNewSaveLogic = useFeatureFlag(
    FeatureFlagsEnum.WebsiteNewSaveLogic,
  )

  const websiteName = useWebsiteSettings(getWebsiteName)
  const websiteDefaultLocale = useWebsiteSettings(getWebsiteLocale)

  const websiteDataBaseline = useWebsiteSettings(getWebsiteDataBaseline)

  const websiteDraftTheme = useWebsiteSettings(getWebsiteDraftTheme)
  const websiteSettings = useWebsiteSettingsForSave()
  const updateWebsiteDraftTheme = useUpdateStoreObject(setWebsiteDraftTheme)
  const updateWebsiteTheme = useUpdateStoreObject(setWebsiteTheme)

  return useCallback(
    async (websiteId: number) => {
      if (isWebsiteNewSaveLogic) {
        const dataChanged =
          (websiteName ?? '') !== (websiteDataBaseline?.name ?? '') ||
          (websiteDefaultLocale ?? '') !==
            (websiteDataBaseline?.defaultLocale ?? '')

        const shouldSaveData =
          dataChanged && Boolean(websiteName || websiteDefaultLocale)

        if (!shouldSaveData) {
          return
        }

        await updateWebsite(websiteId, {
          name: websiteName,
          defaultLocale: websiteDefaultLocale,
        })

        dispatch(
          setWebsiteDataBaseline({
            name: websiteName,
            defaultLocale: websiteDefaultLocale,
          }),
        )
      } else {
        if (!websiteSettings) {
          return
        }

        const response = await updateWebsite(websiteId, {
          ...websiteSettings,
          name: websiteName,
          defaultLocale: websiteDefaultLocale,
        })

        if (websiteDraftTheme) {
          updateWebsiteTheme(response.theme)
          updateWebsiteDraftTheme(null)
        }
      }
    },
    [
      dispatch,
      isWebsiteNewSaveLogic,
      updateWebsiteDraftTheme,
      updateWebsiteTheme,
      websiteDataBaseline,
      websiteDefaultLocale,
      websiteDraftTheme,
      websiteName,
      websiteSettings,
    ],
  )
}
